import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { CreditCard, AlertTriangle, Save } from 'lucide-react';
import { toast } from 'sonner';

export function PaymongoSettings({ data, setData, isSaving, onSave, IntegrationStatus }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-900 font-[Poppins]">PayMongo Payment Gateway</h3>
          <p className="text-sm text-slate-500 mt-1">Accept payments via GCash, Maya, cards, and more</p>
        </div>
        <div className="flex items-center gap-3">
          <IntegrationStatus enabled={data.enabled} />
          <Switch checked={data.enabled} onCheckedChange={(checked) => setData(prev => ({ ...prev, enabled: checked }))} data-testid="paymongo-toggle" />
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <Label>Environment</Label>
          <select value={data.environment} onChange={(e) => setData(prev => ({ ...prev, environment: e.target.value }))} className="w-full h-12 mt-1.5 border border-stone-200 rounded-lg px-3" data-testid="paymongo-env">
            <option value="test">Test Mode (Sandbox)</option>
            <option value="live">Live Mode (Production)</option>
          </select>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Public Key</Label>
            <Input placeholder="pk_test_..." value={data.public_key} onChange={(e) => setData(prev => ({ ...prev, public_key: e.target.value }))} className="h-12 mt-1.5 font-mono text-sm" data-testid="paymongo-public-key" />
          </div>
          <div>
            <Label>Secret Key</Label>
            <Input type="password" placeholder="sk_test_..." value={data.secret_key} onChange={(e) => setData(prev => ({ ...prev, secret_key: e.target.value }))} className="h-12 mt-1.5 font-mono text-sm" data-testid="paymongo-secret-key" />
          </div>
        </div>
        <div>
          <Label>Webhook Secret (Optional)</Label>
          <Input type="password" placeholder="whsec_..." value={data.webhook_secret} onChange={(e) => setData(prev => ({ ...prev, webhook_secret: e.target.value }))} className="h-12 mt-1.5 font-mono text-sm" data-testid="paymongo-webhook" />
          <p className="text-xs text-slate-500 mt-1">For receiving payment notifications</p>
        </div>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-blue-800 font-semibold">Webhook Endpoint URL</Label>
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/paymongo`); toast.success('Webhook URL copied!'); }} className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700">Copy URL</button>
          </div>
          <div className="bg-white rounded-lg p-3 font-mono text-sm text-slate-700 break-all border border-blue-200">{window.location.origin}/api/webhooks/paymongo</div>
          <div className="mt-3 text-xs text-blue-700 space-y-1">
            <p className="font-semibold">Setup Instructions:</p>
            <ol className="list-decimal list-inside space-y-0.5 text-blue-600">
              <li>Go to PayMongo Dashboard - Developers - Webhooks</li>
              <li>Click "Create Webhook"</li>
              <li>Paste the URL above</li>
              <li>Select events: <code className="bg-blue-100 px-1 rounded">checkout_session.payment.paid</code></li>
              <li>Copy the "Signing Secret" and paste in Webhook Secret field above</li>
              <li>Save the webhook in PayMongo</li>
            </ol>
          </div>
        </div>
      </div>
      <div className="mt-6 pt-6 border-t border-stone-200">
        <h4 className="font-semibold text-slate-900 mb-1">Pass-on Fee Settings</h4>
        <p className="text-sm text-slate-500 mb-4">Configure payment processing fees that will be passed on to customers</p>
        <div className="space-y-4">
          {[
            { key: 'card', label: 'Card Payments', icon: <CreditCard className="w-4 h-4 text-slate-600" />, bg: 'bg-slate-50' },
            { key: 'gcash', label: 'GCash', icon: <span className="w-4 h-4 bg-blue-500 rounded text-white text-xs flex items-center justify-center font-bold">G</span>, bg: 'bg-blue-50' },
            { key: 'maya', label: 'Maya', icon: <span className="w-4 h-4 bg-green-500 rounded text-white text-xs flex items-center justify-center font-bold">M</span>, bg: 'bg-green-50' }
          ].map(({ key, label, icon, bg }) => (
            <div key={key} className={`${bg} rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-3">{icon}<span className="font-medium text-slate-700">{label}</span></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">MDR (%)</Label>
                  <Input type="number" step="0.1" min="0" max="10" value={data.fee_settings?.[key]?.mdr || 0} onChange={(e) => setData(prev => ({ ...prev, fee_settings: { ...prev.fee_settings, [key]: { ...prev.fee_settings?.[key], mdr: parseFloat(e.target.value) || 0 } } }))} className="h-10 mt-1" data-testid={`paymongo-${key}-mdr`} />
                </div>
                <div>
                  <Label className="text-xs">Fixed Fee (P)</Label>
                  <Input type="number" step="1" min="0" value={data.fee_settings?.[key]?.fixed || 0} onChange={(e) => setData(prev => ({ ...prev, fee_settings: { ...prev.fee_settings, [key]: { ...prev.fee_settings?.[key], fixed: parseFloat(e.target.value) || 0 } } }))} className="h-10 mt-1" data-testid={`paymongo-${key}-fixed`} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800 font-medium">Fee Calculation Formula</p>
              <p className="text-sm text-amber-700 mt-1">Total = (Amount + Fixed Fee) / (1 - MDR%)</p>
              <p className="text-xs text-amber-600 mt-2">Example: P100 top-up with 3.5% + P15 = P(100 + 15) / (1 - 0.035) = P119.17</p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
        <p className="text-sm text-blue-800"><strong>Get your keys:</strong> Sign up at <a href="https://dashboard.paymongo.com" target="_blank" rel="noopener noreferrer" className="underline">dashboard.paymongo.com</a> and navigate to Developers - API Keys</p>
      </div>
      <Button onClick={onSave} disabled={isSaving} className="mt-6 bg-slate-900 hover:bg-slate-800" data-testid="save-paymongo-btn">
        <Save className="w-4 h-4 mr-2" />{isSaving ? 'Saving...' : 'Save PayMongo Settings'}
      </Button>
    </div>
  );
}
